import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Upload, Eye, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { API_URL, fmtUGX, viewReceipt } from './feesUtils';

const EMPTY = {
  student_id: '', category: '', amount: '', date: new Date().toISOString().slice(0, 10),
  payment_method: '', reference_number: '', receipt_number: '', notes: '',
  receipt_data: '', receipt_name: '', receipt_override: false, override_reason: '',
};

export const RecordPaymentTab = ({ config, school, year, term, refresh, refreshKey }) => {
  const { user } = useAuth();
  const [classSel, setClassSel] = useState('');
  const [students, setStudents] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [recent, setRecent] = useState([]);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const classes = config?.schools?.find((s) => s.key === school)?.classes || [];
  const isAdmin = ['super_admin', 'admin'].includes(user?.role);

  useEffect(() => { setClassSel(''); setStudents([]); }, [school]);

  useEffect(() => {
    if (!classSel) return;
    axios.get(`${API_URL}/api/fees/tracker`, { params: { school, academic_year: year, term, class_name: classSel } })
      .then((r) => setStudents(r.data.rows)).catch(() => {});
  }, [classSel, school, year, term, refreshKey]);

  useEffect(() => {
    axios.get(`${API_URL}/api/fees/payments`, { params: { school, academic_year: year, term } })
      .then((r) => setRecent((r.data.items || []).slice(0, 15))).catch(() => {});
  }, [school, year, term, refreshKey]);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(pdf|jpe?g|png)$/i.test(file.name)) {
      toast.error('Receipt must be PDF, JPG or PNG');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((p) => ({ ...p, receipt_data: reader.result, receipt_name: file.name }));
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/fees/payments`, {
        ...form,
        academic_year: year, term,
        amount: parseFloat(form.amount),
        receipt_data: form.receipt_data || null,
        override_reason: form.receipt_override ? form.override_reason : null,
      });
      toast.success('Payment recorded — balances updated automatically');
      setForm({ ...EMPTY, date: form.date });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const doVoid = async () => {
    try {
      await axios.post(`${API_URL}/api/fees/payments/${voidTarget}/void`, { reason: voidReason });
      toast.success('Payment voided (record kept for audit)');
      setVoidTarget(null);
      setVoidReason('');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to void');
    }
  };

  const selStudent = students.find((s) => s.student_id === form.student_id);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Record a Payment</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Class *</Label>
                <Select value={classSel} onValueChange={setClassSel}>
                  <SelectTrigger data-testid="pay-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
                  <SelectContent>{classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Student *</Label>
                <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                  <SelectTrigger data-testid="pay-student-select"><SelectValue placeholder={classSel ? 'Select student' : 'Pick class first'} /></SelectTrigger>
                  <SelectContent>
                    {students.map((s) => <SelectItem key={s.student_id} value={s.student_id}>{s.student_name}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            </div>
            {selStudent && (
              <div className="text-xs bg-muted/50 rounded-md p-2 flex flex-wrap gap-3" data-testid="pay-student-summary">
                <span>Expected: <strong>{fmtUGX(selStudent.expected_fees)}</strong></span>
                <span>Paid: <strong className="text-green-600">{fmtUGX(selStudent.total_paid)}</strong></span>
                <span>Balance: <strong className="text-red-600">{fmtUGX(selStudent.current_balance)}</strong></span>
                <span>Arrears: <strong className="text-orange-600">{fmtUGX(selStudent.previous_arrears)}</strong></span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Payment Category *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="pay-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {(config?.categories || []).map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Amount (UGX) *</Label>
                <Input type="number" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="pay-amount" /></div>
              <div className="space-y-1.5"><Label>Payment Date *</Label>
                <Input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="pay-date" /></div>
              <div className="space-y-1.5"><Label>Payment Method *</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger data-testid="pay-method-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{(config?.payment_methods || []).map((m) => <SelectItem key={m} value={m} className="capitalize">{m.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Receipt Number</Label>
                <Input value={form.receipt_number} onChange={(e) => setForm({ ...form, receipt_number: e.target.value })} data-testid="pay-receipt-number" /></div>
              <div className="space-y-1.5"><Label>Reference Number</Label>
                <Input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} data-testid="pay-reference" /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Receipt Upload * <span className="text-xs text-muted-foreground">(PDF / JPG / PNG — mandatory)</span></Label>
              <label className="flex items-center justify-center gap-2 px-3 py-2 border rounded-md cursor-pointer hover:bg-muted text-sm">
                <Upload className="h-4 w-4" /> <span>{form.receipt_name || 'Upload payment receipt'}</span>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={onFile} data-testid="pay-receipt-upload" />
              </label>
              {isAdmin && !form.receipt_data && (
                <div className="flex items-center gap-2 pt-1">
                  <Checkbox checked={form.receipt_override} onCheckedChange={(c) => setForm({ ...form, receipt_override: !!c })}
                    id="ovr" data-testid="pay-override-check" />
                  <Label htmlFor="ovr" className="text-xs cursor-pointer">Admin override — save without receipt (audited)</Label>
                </div>
              )}
              {form.receipt_override && !form.receipt_data && (
                <Input placeholder="Override reason (required)" value={form.override_reason}
                  onChange={(e) => setForm({ ...form, override_reason: e.target.value })} data-testid="pay-override-reason" />
              )}
            </div>
            <div className="space-y-1.5"><Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="pay-notes" /></div>
            <Button type="submit" className="w-full" data-testid="pay-submit-btn"
              disabled={saving || !form.student_id || !form.category || !form.payment_method}>
              {saving ? 'Saving…' : 'Save & Confirm Payment'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="font-heading text-base">Recent Payments — {year} Term {term}</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Student</TableHead><TableHead>Category</TableHead>
              <TableHead className="text-right">Amount</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {recent.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No payments yet</TableCell></TableRow>
              ) : recent.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="text-xs">{p.date}</TableCell>
                  <TableCell className="font-medium">{p.student_name}</TableCell>
                  <TableCell><Badge variant="outline" className="capitalize">{p.category.replace(/_/g, ' ')}</Badge></TableCell>
                  <TableCell className="text-right">{fmtUGX(p.amount)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => viewReceipt(p.id)} data-testid={`view-receipt-${p.id}`}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-destructive" onClick={() => setVoidTarget(p.id)} data-testid={`void-payment-${p.id}`}>
                        <Ban className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {voidTarget && (
            <div className="p-3 border-t flex items-center gap-2" data-testid="void-form">
              <Input placeholder="Void reason (required)" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} data-testid="void-reason" />
              <Button size="sm" variant="destructive" disabled={!voidReason.trim()} onClick={doVoid} data-testid="void-confirm">Void</Button>
              <Button size="sm" variant="outline" onClick={() => setVoidTarget(null)}>Cancel</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
